import React from 'react';
import WorkOffIcon from '@mui/icons-material/WorkOff';
import AccessibleIcon from '@mui/icons-material/Accessible';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { Avatar, Tooltip, useTheme } from '@mui/material';
import { Currency } from '../../types/account';
import { InsuranceType } from './types';

export const insuranceLogoSwitch = (params: {
  value: string;
  invertColors?: boolean;
  iconSize: 'small' | 'medium';
}) => {
  const { value, invertColors, iconSize } = params;
  const theme = useTheme();
  switch (value) {
    case 'unemployment':
    case 'unemployment-mhe-mortgage':
      return (
        <WorkOffIcon
          key={value}
          style={{
            fontSize: iconSize === 'medium' ? 17 : 13,
            color: invertColors ? 'whitesmoke' : theme.palette.primary.main,
          }}
        />
      );
    case 'incapacity':
    case 'incapacity-mhe-mortgage':
      return (
        <AccessibleIcon
          key={value}
          style={{
            fontSize: iconSize === 'medium' ? 20 : 16,
            color: invertColors ? 'whitesmoke' : theme.palette.primary.main,
          }}
        />
      );
    case 'death':
    case 'death-mhe-mortgage':
      return (
        <FavoriteIcon
          key={value}
          style={{
            fontSize: iconSize === 'medium' ? 18 : 14,
            color: invertColors ? 'whitesmoke' : theme.palette.primary.main,
          }}
        />
      );
    case 'disaster-mhe-mortgage':
      return (
        <LocalFireDepartmentIcon
          key={value}
          style={{
            fontSize: iconSize === 'medium' ? 18 : 14,
            color: invertColors ? 'whitesmoke' : theme.palette.primary.main,
          }}
        />
      );
    default:
      return (
        <VerifiedUserIcon
          key={value}
          style={{
            fontSize: iconSize === 'medium' ? 18 : 14,
            color: invertColors ? 'whitesmoke' : theme.palette.primary.main,
          }}
        />
      );
  }
};

export const getInsuranceLogo = (params: {
  value: string;
  label: string;
  invertColors?: boolean;
  iconSize: 'small' | 'medium';
}) => {
  const {
    value, label, invertColors, iconSize,
  } = params;
  const theme = useTheme();
  const avatarSizeSwitch = (size: 'small' | 'medium') => {
    switch (size) {
      case 'small':
        return 24;
      default:
        return 30;
    }
  };
  return (
    <Tooltip
      key={value}
      title={label}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'grey',
            '& .MuiTooltip-arrow': {
              color: 'common.grey',
            },
            color: 'white',
          },
        },
      }}
    >
      <Avatar
        style={{
          width: avatarSizeSwitch(iconSize),
          height: avatarSizeSwitch(iconSize),
          backgroundColor: invertColors
            ? theme.palette.primary.main
            : 'transparent',
        }}
      >
        {insuranceLogoSwitch({ value, invertColors, iconSize })}
      </Avatar>
    </Tooltip>
  );
};

export const calculateInsuranceCost = (params: {
  insurance: InsuranceType;
  currency: Currency;
  periods: number;
  loanAmount: number;
  installment: number;
}) => {
  const {
    insurance, periods, loanAmount, currency, installment,
  } = params;
  if (insurance.type === 'unemployment-mhe-mortgage') {
    return installment * insurance.premiumMultiplier * periods;
  }
  if (
    [
      'death-downpayment',
      'incapacity-mhe-mortgage',
      'disaster-mhe-mortgage',
      'death-mhe-mortgage',
    ].includes(insurance.type)
  ) {
    return (
      Math.round(
        (insurance.premiumMultiplier * loanAmount + Number.EPSILON)
          * 10 ** currency.precision,
      )
      / 10 ** currency.precision
    );
  }
  return (
    Math.round(
      (insurance.premiumMultiplier * periods * loanAmount + Number.EPSILON)
          * 10 ** currency.precision,
    )
      / 10 ** currency.precision
  );
};
